import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  Watch,
  State,
  AttachInternals,
} from '@stencil/core';
import { keyboardClickProps } from '../../utils/keyboard';
import { Size, Kind, DeprecatedKind, Type } from './types';

export interface AhaButton extends HTMLAhaButtonElement {}

/**
 * @slot DEFAULT - Contents of the `<aha-button>` element.
 */
@Component({
  tag: 'aha-button',
  styleUrl: 'aha-button.css',
  shadow: true,
  formAssociated: true,
})
export class AhaButton {
  @Element() host!: HTMLAhaButtonElement;

  /** The appearance of the button. The "default" kind is the default style. */
  @Prop({ reflect: true }) kind: Kind | DeprecatedKind = 'default';

  /** The size of the button. */
  @Prop({ reflect: true }) size: Size = 'medium';

  /** Whether or not the button is currently in an "active" state. Changes the styling of the button. */
  @Prop({ reflect: true, mutable: true }) active: boolean = false;

  @Watch('active')
  private activateChildInputElements() {
    this.host
      .querySelectorAll('input[type="checkbox"], input[type="radio"]')
      .forEach((elem: any) => {
        elem.checked = this.active;
        this.active ? this.host.setAttribute('active', '') : this.host.removeAttribute('active');

        var event = new window.Event('change', { bubbles: true });
        elem.dispatchEvent(event);
      });
  }

  /** An alternate appearance that removes the button's background. */
  @Prop({ reflect: true }) outline: boolean = false;

  /** Whether or not the <aha-button> is disabled. */
  @Prop({ reflect: true }) disabled: boolean = false;
  @Watch('disabled')
  private watchDisabled() {
    if (this.disabled) {
      const tabindex = this.host.getAttribute('tabindex');
      this.originalTabIndex = typeof tabindex === 'undefined' ? undefined : Number(tabindex);
    }
  }

  /** The type of button. The default is a plain "button", which does not trigger any form submit events when clicked. */
  @Prop({ reflect: true }) type: Type = 'button';

  /** A URL string that turns the button into a clickable link, directing the user to a different URL. */
  @Prop() href: string | undefined = undefined;

  /** Whether or not to open an `<aha-button>` with an `href` attribute in a new window. */
  @Prop() target: '_blank' | undefined;

  /** If present, the button will be disabled and display the given text on click. **/
  @Prop() disableWith: string | undefined = undefined;

  @State() originalTabIndex: number | undefined = undefined;

  @AttachInternals() private internals!: ElementInternals;

  /** Event that we dispatch when a button has been clicked. We pass the host element as the event detail. */
  @Event({
    eventName: 'aha-button:click',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  buttonClicked!: EventEmitter<HTMLAhaButtonElement>;

  private handleKeyDown = (event: KeyboardEvent) => {
    if ([' ', 'Enter'].includes(event.key)) {
      this.active = true;
    }
  };

  private handleKeyUp = (event: KeyboardEvent) => {
    if ([' ', 'Enter'].includes(event.key)) {
      this.active = false;

      this.followLinkOrSubmitForm(event);

      if (this.disableWith) {
        this.disabled = true;
        this.setContent(this.disableWith);
      }
    }
  };

  private handleClick = (event: Event) => {
    if (this.disabled) return;

    this.followLinkOrSubmitForm(event);

    if (this.disableWith) {
      this.disabled = true;
      this.setContent(this.disableWith);
    }

    // Fire an event on the document to let any listener know that the button has been clicked.
    this.buttonClicked.emit(this.host);
  };

  private followLinkOrSubmitForm = (event: Event) => {
    if (this.type === 'submit' && !this.disabled) {
      const form = this.internals.form;
      if (form && !form.disabled) {
        form.requestSubmit();
      }
    } else if (this.href) {
      if ((event as KeyboardEvent).metaKey || this.target === '_blank') {
        window.open(this.href, '_blank', 'noopener,noreferrer');
      } else if (typeof window.Turbolinks?.visit === 'function') {
        window.Turbolinks?.visit(this.href);
      } else {
        location.assign(this.href);
      }
    }
  };

  private async setContent(newContent: string) {
    const content = this.host.shadowRoot?.querySelector<HTMLElement>('#content');
    if (content) {
      content.textContent = newContent;
    }
  }

  componentDidLoad() {
    if (this.host.hasAttribute('active')) {
      this.active = true;
    }
  }

  formAssociatedCallback(form: HTMLFormElement | null) {
    // When there is a single input in a form, pushing Enter in that field will submit the form.
    // When there are multiple inputs in a form, pushing Enter in a field will not submit the form, unless there
    // is also an <input type="submit"> in the form.
    // This is a workaround since aha-button does not render an input field and to avoid lifting anything to the
    // light DOM, we instead bind an event listener to the form to handle submit when Enter is pressed
    if (form && this.type === 'submit') {
      form.addEventListener('keyup', (event: KeyboardEvent) => {
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
          this.handleClick(event);
          event.preventDefault();
        }
      });
    }
  }

  render() {
    const tabIndex = this.disabled
      ? -1
      : typeof this.originalTabIndex === 'undefined'
      ? 0
      : this.originalTabIndex;

    return (
      <Host
        exportparts="content"
        tabindex={tabIndex}
        role={this.href ? 'link' : 'button'}
        {...keyboardClickProps({ onKeyDown: this.handleKeyDown })}
        onKeyUp={this.handleKeyUp}
        onClick={this.handleClick}
      >
        <div id="content" part="content">
          <slot></slot>
        </div>
      </Host>
    );
  }
}
